<template>
  <div class="custom-fields-manager">
    <entity-settings-loading v-if="isLoading" />
    <template v-else>
      <p>
        Add custom fields to capture extra information on every {{ itemLabel }} in this {{ entityLabel }}.
        Choose who can see and edit each field, and drag fields to change the order they appear in.
      </p>
      <div class="dd-divider" />
      <div class="row">
        <div class="col-12 custom-fields-list">
          <div class="custom-fields-list__header">
            <h4>Fields</h4>
            <dd-icon-button
              :is-disabled="isSubmitting"
              icon="plus"
              type="circle"
              size="medium"
              title="Add custom field"
              @click="beginCreateField()"
            />
          </div>
          <draggable
            v-if="hasFields"
            :animation="180"
            :scroll-sensitivity="100"
            :scroll-speed="20"
            :force-fallback="true"
            :list="customFields"
            :handle="'.drag-handle'"
            :disabled="isSubmitting"
            drag-class="drag-card"
            item-key="id"
            @end="saveSortOrder"
          >
            <template #item="{ element: field }">
              <div class="custom-fields-list__row">
                <span class="drag-handle" title="Drag to reorder" @mousedown.prevent><i class="ddi ddi-sort" /></span>
                <i :class="`ddi ddi-${CustomFieldAssistant.getFieldTypeIcon(field.customFieldType)}`" class="custom-fields-list__type-icon" />
                <div class="custom-fields-list__details">
                  <span class="custom-fields-list__name">{{ field.name }}</span>
                  <span class="custom-fields-list__meta">
                    {{ CustomFieldAssistant.getFieldTypeName(field.customFieldType) }}
                    &middot; Users: {{ CustomFieldAssistant.getPermissionShortName(field.normalUserPermissions) }}
                    <template v-if="showGuestPermissions">
                      &middot; Guests: {{ CustomFieldAssistant.getPermissionShortName(field.guestUserPermissions) }}
                    </template>
                  </span>
                  <div v-if="field.customFieldType === ECustomFieldTypes.Dropdown" class="custom-fields-list__options">
                    <chicklet v-for="option in field.options" :key="option.id" :color="option.color" size="tiny">{{ option.name }}</chicklet>
                  </div>
                </div>
                <div class="custom-fields-list__actions">
                  <dd-icon-button
                    :is-disabled="isSubmitting"
                    icon="edit"
                    type="rounded"
                    size="medium"
                    color="grey"
                    title="Edit custom field"
                    @click="beginEditField(field.id)"
                  />
                  <dd-icon-button
                    :is-disabled="isSubmitting"
                    danger
                    icon="trash"
                    type="rounded"
                    size="medium"
                    color="grey"
                    title="Delete custom field"
                    @click="removeField(field)"
                  />
                </div>
              </div>
            </template>
          </draggable>
          <p v-else class="custom-fields-list__empty-message">
            There are no custom fields for this {{ entityLabel }}.
            <a href="#" class="just-a-link" @click.stop.prevent="beginCreateField()">Add your first</a>!
          </p>
        </div>
      </div>

      <!-- Modal -->
      <dd-modal
        v-model:visible="modalOpened"
        :title="isEditing ? 'Edit Custom Field' : 'New Custom Field'"
        @handle-close="closeModal"
      >
        <div class="custom-fields-manager__tabs">
          <div
            :class="{ 'selected': activeTab === ModalTabs.Settings }"
            @click="activeTab = ModalTabs.Settings"
          ><i class="ddi ddi-cog" />&nbsp;Field Settings</div>
          <div
            :class="{ 'selected': activeTab === ModalTabs.Permissions }"
            @click="activeTab = ModalTabs.Permissions"
          ><i class="ddi ddi-lock" />&nbsp;Permissions</div>
          <div
            :class="{ 'selected': activeTab === ModalTabs.Display }"
            @click="activeTab = ModalTabs.Display"
          ><i class="ddi ddi-watching" />&nbsp;Display Settings</div>
        </div>
        <el-form ref="customFieldForm" :model="editingField" :rules="rules" label-position="top" @submit.prevent>
          <!-- FIELD SETTINGS TAB -->
          <div v-show="activeTab === ModalTabs.Settings" class="custom-fields-manager__tab-pane">
            <!-- Name -->
            <el-form-item label="Name" prop="name">
              <el-input
                ref="customFieldForm_Name"
                v-model="editingField.name"
                placeholder="e.g. Estimated hours"
                maxlength="100"
              />
            </el-form-item>

            <!-- Type -->
            <el-form-item label="Field type" prop="customFieldType">
              <single-select
                v-model="editingField.customFieldType"
                :disabled="isEditing || isSubmitting"
                @change="handleFieldTypeChange"
              >
                <el-option v-for="type in CustomFieldAssistant.FieldTypeOptions" :key="type.id" :value="type.id" :label="type.name" />
              </single-select>
              <p v-if="isEditing" class="custom-fields-manager__hint">
                The field type can't be changed once a field has been created. Delete this field and add a new one to use a different type.
              </p>
            </el-form-item>

            <!-- Text settings -->
            <template v-if="editingField.customFieldType === ECustomFieldTypes.Text">
              <el-form-item label="Text format" prop="textType">
                <single-select v-model="editingField.textType" :disabled="isSubmitting">
                  <el-option v-for="textType in CustomFieldAssistant.TextTypeOptions" :key="textType.id" :value="textType.id" :label="textType.name" />
                </single-select>
              </el-form-item>
              <div v-if="editingField.textType === ECustomFieldTextTypes.Unformatted" class="custom-fields-manager__columns">
                <el-form-item label="Minimum length" prop="textMinLength">
                  <el-input v-model.number="editingField.textMinLength" :disabled="isSubmitting" type="number" min="0" placeholder="No minimum" />
                </el-form-item>
                <el-form-item label="Maximum length" prop="textMaxLength">
                  <el-input v-model.number="editingField.textMaxLength" :disabled="isSubmitting" type="number" min="0" placeholder="No maximum" />
                </el-form-item>
              </div>
            </template>

            <!-- Number settings -->
            <template v-else-if="editingField.customFieldType === ECustomFieldTypes.Number">
              <div class="custom-fields-manager__columns">
                <el-form-item label="Number format" prop="numberType">
                  <single-select v-model="editingField.numberType" :disabled="isSubmitting">
                    <el-option v-for="numberType in CustomFieldAssistant.NumberTypeOptions" :key="numberType.id" :value="numberType.id" :label="numberType.name" />
                  </single-select>
                </el-form-item>
                <el-form-item label="Decimal places" prop="numberDecimalPlaces">
                  <el-input v-model.number="editingField.numberDecimalPlaces" :disabled="isSubmitting" type="number" min="0" max="10" />
                </el-form-item>
              </div>
              <div class="custom-fields-manager__columns">
                <el-form-item label="Minimum value" prop="numberMinValue">
                  <el-input v-model.number="editingField.numberMinValue" :disabled="isSubmitting" type="number" placeholder="No minimum" />
                </el-form-item>
                <el-form-item label="Maximum value" prop="numberMaxValue">
                  <el-input v-model.number="editingField.numberMaxValue" :disabled="isSubmitting" type="number" placeholder="No maximum" />
                </el-form-item>
              </div>
              <div v-if="editingField.numberType !== ECustomFieldNumberTypes.Percentage" class="custom-fields-manager__columns">
                <el-form-item label="Label" prop="numberLabel">
                  <el-input v-model="editingField.numberLabel" :disabled="isSubmitting" placeholder="e.g. $ or hrs" maxlength="10" />
                </el-form-item>
                <el-form-item label="Label position" prop="numberLabelPosition">
                  <single-select v-model="editingField.numberLabelPosition" :disabled="isSubmitting">
                    <el-option v-for="position in CustomFieldAssistant.NumberLabelPositionOptions" :key="position.id" :value="position.id" :label="position.name" />
                  </single-select>
                </el-form-item>
              </div>
            </template>

            <!-- Dropdown settings -->
            <template v-else-if="editingField.customFieldType === ECustomFieldTypes.Dropdown">
              <label class="el-form-item__label">Options</label>
              <draggable
                :animation="180"
                :scroll-sensitivity="100"
                :scroll-speed="20"
                :force-fallback="true"
                :list="editingField.options"
                :handle="'.drag-handle'"
                drag-class="drag-card"
                item-key="key"
              >
                <template #item="{ element: option, index }">
                  <el-form-item>
                    <div class="input-with-trash">
                      <workflow-status-color-picker v-model:color="option.color" :color-size-in-pixels="16" />
                      <el-input
                        v-model="option.name"
                        :disabled="isSubmitting"
                        placeholder="e.g. High"
                        maxlength="50"
                      />
                      <span class="drag-handle" @mousedown.prevent><i class="ddi ddi-sort" /></span>
                      <i class="ddi ddi-trash scary-list-option" @click="editingField.options.splice(index, 1)" />
                    </div>
                  </el-form-item>
                </template>
              </draggable>
              <dd-button type="text" @click="addOption">+ Add option</dd-button>
            </template>
          </div>

          <!-- PERMISSIONS TAB -->
          <div v-show="activeTab === ModalTabs.Permissions" class="custom-fields-manager__tab-pane">
            <p class="custom-fields-manager__tab-intro">
              Choose who can see this field on each {{ itemLabel }} and who can change its value.
              Admins and owners can always view and edit every field.
            </p>
            <el-form-item label="Normal users can" prop="normalUserPermissions">
              <single-select v-model="editingField.normalUserPermissions" :disabled="isSubmitting">
                <el-option v-for="permission in CustomFieldAssistant.PermissionOptions" :key="permission.id" :value="permission.id" :label="permission.name" />
              </single-select>
            </el-form-item>
            <el-form-item v-if="showGuestPermissions" label="Guests can" prop="guestUserPermissions">
              <single-select v-model="editingField.guestUserPermissions" :disabled="isSubmitting">
                <el-option v-for="permission in CustomFieldAssistant.PermissionOptions" :key="permission.id" :value="permission.id" :label="permission.name" />
              </single-select>
            </el-form-item>
          </div>

          <!-- DISPLAY SETTINGS TAB -->
          <div v-show="activeTab === ModalTabs.Display" class="custom-fields-manager__tab-pane">
            <p class="custom-fields-manager__tab-intro">
              Choose where this field appears beyond the {{ itemLabel }} detail page.
            </p>
            <el-form-item prop="showOnListings">
              <dd-checkbox v-model="editingField.showOnListings" :disabled="isSubmitting" dd-size="medium">
                Show this field on {{ itemLabel }} listings
              </dd-checkbox>
              <p class="custom-fields-manager__hint">
                When checked, the field's value is shown on the {{ entityLabel }}'s {{ itemLabel }} list.
                People who can't view the field still won't see it.
              </p>
            </el-form-item>
            <el-form-item prop="showOnCreate">
              <dd-checkbox v-model="editingField.showOnCreate" :disabled="isSubmitting" dd-size="medium">
                Show this field on the {{ itemLabel }} creation
              </dd-checkbox>
              <p class="custom-fields-manager__hint">
                When checked, the field will appear on the {{ entityLabel }}'s {{ itemLabel }} creation form.
                People who can't view the field still won't see it.
              </p>
            </el-form-item>
          </div>
        </el-form>
        <template #footer>
          <span>
            <dd-button type="text" @click="closeModal">Cancel</dd-button>
            <dd-button :color="buttonColor" :is-disabled="isSubmitting" @click="saveField">{{ isEditing ? 'Update Field' : 'Add Field' }}</dd-button>
          </span>
        </template>
      </dd-modal>
    </template>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import draggable from 'vuedraggable'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'
import CustomFieldAssistant, {
  ECustomFieldNumberTypes,
  ECustomFieldPermissions,
  ECustomFieldTextTypes,
  ECustomFieldTypes,
} from '@/helpers/custom-field-assistant'

/* Data types */
import {
  ICustomField,
  ICustomFieldOption,
  IGenericJSONMessage,
  IProjectWithWorkflow,
} from '@/api/types/response.types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'

/* The subset of the mailbox / internal project API used here. Both modules expose identical signatures. */
interface ICustomFieldsAPI {
  get_WithWorkflow(id: any): Promise<IProjectWithWorkflow>
  get_CustomFields(id: any): Promise<ICustomField[]>
  post_CustomFieldCreate(
    id: any,
    data: { customField: ICustomField },
  ): Promise<ICustomField>
  put_CustomFieldUpdate(
    id: any,
    customFieldId: any,
    data: { customField: ICustomField },
  ): Promise<ICustomField>
  put_CustomFieldsUpdateSort(
    id: any,
    sortedCustomFieldIds: number[],
  ): Promise<IGenericJSONMessage>
  delete_CustomFieldDelete(
    id: any,
    customFieldId: any,
  ): Promise<IGenericJSONMessage>
}

/* Options get a client-side key so the draggable list stays stable while options are added, removed and reordered. */
interface IEditableOption extends ICustomFieldOption {
  key: number
}

interface IEditableField extends ICustomField {
  options: IEditableOption[]
}

enum ModalTabs {
  Settings = 'settings',
  Permissions = 'permissions',
  Display = 'display',
}

export default defineComponent({
  name: 'CustomFieldsManager',

  components: {
    EntitySettingsLoading,
    draggable,
  },

  props: {
    /* ID of the project or mailbox */
    id: {
      type: Number,
      required: true,
    },
    /* 'project' or 'mailbox' */
    entityType: {
      type: String,
      required: true,
      validator: (value: string) => ['project', 'mailbox'].includes(value),
    },
  },

  emits: ['name-loaded'],

  data() {
    return {
      CustomFieldAssistant,
      ECustomFieldTypes,
      ECustomFieldNumberTypes,
      ECustomFieldTextTypes,
      isLoading: true,
      isSubmitting: false,
      editingID: null as number,
      customFields: [] as ICustomField[],
      editingField: CustomFieldAssistant.createEmptyField() as IEditableField,
      modalOpened: false,
      nextOptionKey: 1,
      ModalTabs,
      activeTab: ModalTabs.Settings,
      rules: {
        name: [
          {
            required: true,
            trigger: 'submit',
            message: 'Name is required',
          },
        ],
      },
    }
  },

  computed: {
    api(): ICustomFieldsAPI {
      return this.entityType === 'mailbox' ? MailboxesAPI : InternalProjectsAPI
    },
    entityLabel(): string {
      return this.entityType
    },
    itemLabel(): string {
      return this.entityType === 'mailbox' ? 'conversation' : 'task'
    },
    /* Guests only exist on projects, so mailboxes never show or set guest permissions */
    showGuestPermissions(): boolean {
      return this.entityType !== 'mailbox'
    },
    buttonColor(): string {
      return this.entityType === 'mailbox'
        ? ColorAssistant.MailboxColor
        : ColorAssistant.ProjectColor
    },
    isEditing(): boolean {
      return this.editingID != null
    },
    hasFields(): boolean {
      return this.customFields.length > 0
    },
  },

  watch: {
    modalOpened(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.customFieldForm_Name.focus()
        })
      }
    },
  },

  async created() {
    this.isLoading = true
    await this.getCustomFields()
    this.isLoading = false
  },

  methods: {
    async getCustomFields() {
      try {
        const projectInfoResponse = await this.api.get_WithWorkflow(this.id)
        this.$emit('name-loaded', projectInfoResponse.title)
        this.customFields = await this.api.get_CustomFields(this.id)
      } catch (error) {
        this.$error.emit(error as any)
      }
    },

    beginCreateField(): void {
      this.editingID = null
      this.editingField = this.toEditableField(
        CustomFieldAssistant.createEmptyField(),
      )
      this.activeTab = ModalTabs.Settings
      this.modalOpened = true
    },

    beginEditField(id: number): void {
      const field = this.customFields.find(f => f.id === id)
      if (!field) {
        return
      }
      this.editingID = id
      // Work on a copy so cancelling the modal leaves the list untouched
      this.editingField = this.toEditableField(
        JSON.parse(JSON.stringify(field)),
      )
      this.activeTab = ModalTabs.Settings
      this.modalOpened = true
    },

    toEditableField(field: ICustomField): IEditableField {
      return {
        ...field,
        options: (field.options || []).map(option => ({
          ...option,
          key: this.nextOptionKey++,
        })),
      }
    },

    handleFieldTypeChange(): void {
      if (
        this.editingField.customFieldType === ECustomFieldTypes.Dropdown &&
        this.editingField.options.length === 0
      ) {
        this.addOption()
      }
    },

    addOption(): void {
      this.editingField.options.push({
        ...CustomFieldAssistant.createEmptyOption(
          this.editingField.options.length,
        ),
        key: this.nextOptionKey++,
      })
    },

    /* Turns the edited field into the payload the API expects: blank numeric inputs become null, options are re-sorted. */
    buildPayload(): ICustomField {
      const toNullableNumber = (value: any): number | null =>
        value === '' || value === null || value === undefined
          ? null
          : Number(value)

      // Percentages always render with a % sign, so any label entered before switching format is dropped
      const isPercentage =
        this.editingField.numberType === ECustomFieldNumberTypes.Percentage

      // Length limits only apply to free-form text; email, phone and URL formats have their own validation
      const hasLengthLimits =
        this.editingField.textType === ECustomFieldTextTypes.Unformatted

      return {
        ...this.editingField,
        guestUserPermissions: this.showGuestPermissions
          ? this.editingField.guestUserPermissions
          : ECustomFieldPermissions.None,
        numberLabel: isPercentage ? '' : this.editingField.numberLabel,
        numberDecimalPlaces:
          toNullableNumber(this.editingField.numberDecimalPlaces) || 0,
        numberMinValue: toNullableNumber(this.editingField.numberMinValue),
        numberMaxValue: toNullableNumber(this.editingField.numberMaxValue),
        textMinLength: hasLengthLimits
          ? toNullableNumber(this.editingField.textMinLength)
          : null,
        textMaxLength: hasLengthLimits
          ? toNullableNumber(this.editingField.textMaxLength)
          : null,
        options: this.editingField.options.map((option, index) => ({
          id: option.id,
          name: option.name,
          color: option.color,
          sort: index,
        })),
      }
    },

    saveField(): void {
      // @ts-ignore
      this.$refs.customFieldForm.validate(async (valid: boolean) => {
        if (!valid) {
          // The only validated inputs live on the settings tab, so make sure the user can see the message
          this.activeTab = ModalTabs.Settings
          return
        }

        this.isSubmitting = true
        try {
          const payload = this.buildPayload()

          if (this.isEditing) {
            await this.api.put_CustomFieldUpdate(this.id, this.editingID, {
              customField: payload,
            })
            this.$messaging.showSuccess('Custom field has been updated!')
          } else {
            await this.api.post_CustomFieldCreate(this.id, {
              customField: payload,
            })
            this.$messaging.showSuccess('Custom field has been added!')
          }

          this.modalOpened = false
          await this.getCustomFields()
        } catch (error) {
          // Keep the modal open so the user can correct the problem
          this.$error.emit(error as any, true)
        } finally {
          this.isSubmitting = false
        }
      })
    },

    closeModal(): void {
      this.modalOpened = false
      // @ts-ignore
      this.$refs.customFieldForm.clearValidate()
    },

    async removeField(field: ICustomField) {
      const message =
        `Are you sure you want to delete the "${field.name}" field? ` +
        `Any values entered for it on ${
          this.itemLabel
        }s will no longer be shown. ` +
        `This cannot be undone.`

      await this.$confirm(message, {
        center: true,
        confirmButtonText: 'Yes, Delete',
        confirmButtonClass: 'el-button--strawberry',
        cancelButtonText: 'Cancel',
        cancelButtonClass: 'el-button--text',
        type: 'warning',
        title: 'Delete Custom Field',
      })
      await this.deleteField(field.id)
    },

    async deleteField(id: number) {
      try {
        this.isSubmitting = true
        const response = await this.api.delete_CustomFieldDelete(this.id, id)
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error as any, true)
      } finally {
        await this.getCustomFields()
        this.isSubmitting = false
      }
    },

    async saveSortOrder() {
      try {
        this.isSubmitting = true
        await this.api.put_CustomFieldsUpdateSort(
          this.id,
          this.customFields.map(f => f.id),
        )
        this.customFields.forEach((field, index) => {
          field.sort = index
        })
      } catch (error) {
        this.$error.emit(error as any, true)
        await this.getCustomFields()
      } finally {
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.custom-fields-manager {
  &__tabs {
    display: flex;
    margin-bottom: 20px;
    border-bottom: $border--solid-extra-light;

    > div {
      padding: 8px 0 6px;
      margin-right: 24px;
      margin-bottom: -1px;
      cursor: pointer;
      @include lettering--p-sm;
      color: var(--color-text-third);
      font-weight: $bold;
      border-bottom: solid 2px transparent;

      .ddi {
        font-size: 12px;
      }

      &:hover {
        color: var(--color-text-secondary);
      }

      &.selected {
        color: var(--color-text-secondary);
        border-bottom-color: $projects--theme-color;
      }
    }
  }

  &__tab-intro {
    margin: 0 0 16px;
    @include lettering--p-sm;
    color: var(--color-text-third);
  }

  &__hint {
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: var(--color-text-third);
  }

  &__columns {
    display: flex;
    @include flex-row-horizontal-spacing(16px);

    > .el-form-item {
      flex: 1;
      min-width: 0;
    }
  }

  .custom-fields-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    &__empty-message {
      color: var(--color-text-third);
      padding-bottom: 20px;
    }

    &__row {
      display: flex;
      align-items: center;
      padding: 12px 0;
      background-color: var(--color-background-primary);

      &:not(:last-child) {
        border-bottom: $border--solid-extra-light;
      }

      .drag-handle {
        cursor: grab;
        color: var(--color-text-third);
        font-size: 16px;
        margin-right: 10px;
      }
    }

    &__type-icon {
      color: var(--color-text-third);
      font-size: 16px;
    }

    &__details {
      display: flex;
      flex-direction: column;
      margin-left: 10px;
      min-width: 0;
    }

    &__name {
      @include lettering--p;
    }

    &__meta {
      font-size: 12px;
      color: var(--color-text-third);
    }

    &__options {
      display: flex;
      flex-wrap: wrap;
      margin-top: 6px;
      @include flex-row-horizontal-spacing(4px);

      .chicklet {
        margin-bottom: 4px;
      }
    }

    &__actions {
      display: flex;
      margin-left: auto;
      padding-left: 10px;

      > :last-child {
        margin-left: 8px;
      }
    }
  }

  .input-with-trash {
    display: flex;
    align-items: center;

    .workflow-status-color-picker {
      margin-right: 8px;
      flex-shrink: 0;
    }

    .el-input {
      flex: 1;
    }

    .ddi-trash {
      margin-left: 8px;
      cursor: pointer;
      font-size: 18px;
    }

    .drag-handle {
      margin-left: 8px;
      cursor: grab;
      color: var(--color-text-third);
      font-size: 16px;
    }
  }
}

.drag-card {
  cursor: grab !important;
  opacity: 0.95 !important;
  background-color: var(--color-background-primary) !important;
  border: solid 1px $light-blue-grey-two;
}
</style>
